/**
 * Canonical analytics event vocabulary.
 *
 * These GA4-standard event names are the single source of truth for EVERY
 * destination (GA4, Meta, PostHog) and supersede any earlier custom names.
 * See Confluence "Analytics & Goal Tracking" (RED-10690) — one name, emitted
 * consistently everywhere, is what makes web + app + terminal data joinable.
 */
export const AnalyticsEvent = {
  // Authentication
  SignUp: 'sign_up',
  Login: 'login',
  // Browsing & search
  Search: 'search',
  ViewItemList: 'view_item_list',
  ViewItem: 'view_item',
  // Basket
  AddToCart: 'add_to_cart',
  RemoveFromCart: 'remove_from_cart',
  ViewCart: 'view_cart',
  // Checkout funnel
  ViewPromotion: 'view_promotion',
  SelectPromotion: 'select_promotion',
  BeginCheckout: 'begin_checkout',
  AddShippingInfo: 'add_shipping_info',
  AddPaymentInfo: 'add_payment_info',
  // Conversion & loyalty
  Purchase: 'purchase',
  EarnVirtualCurrency: 'earn_virtual_currency',
  // Failure
  PaymentFailed: 'payment_failed',
  // Product/ops only — no GA4 standard equivalent, routed to PostHog only
  GenerateLead: 'generate_lead',
  Contact: 'contact',
  CustomizeProduct: 'customize_product',
} as const

export type AnalyticsEventName =
  (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent]

/**
 * Meta's standard pixel events.
 *
 * https://developers.facebook.com/docs/meta-pixel/reference
 */
export const META_STANDARD_EVENTS = [
  'AddPaymentInfo',
  'AddToCart',
  'AddToWishlist',
  'CompleteRegistration',
  'Contact',
  'CustomizeProduct',
  'Donate',
  'FindLocation',
  'InitiateCheckout',
  'Lead',
  'Purchase',
  'Schedule',
  'Search',
  'StartTrial',
  'SubmitApplication',
  'Subscribe',
  'ViewContent',
] as const

export type MetaStandardEvent = (typeof META_STANDARD_EVENTS)[number]

/**
 * A Meta (Facebook) pixel destination for an event.
 *
 * Standard names are spelling-checked against Meta's reference; anything with
 * no standard equivalent has to opt in to `trackCustom`.
 */
export type MetaDestination = (
  | { name: MetaStandardEvent; custom?: false }
  | { name: string; custom: true }
) & {
  /** Fire only when the payload carries a `contentType`. */
  requiresContentType?: true
}

/**
 * Per-event destination routing, per the "Analytics & Goal Tracking" contract.
 *
 * PostHog receives every event; GA4 receives the full ecommerce funnel; Meta
 * receives the events that have a standard pixel equivalent, under its own
 * names. Non-standard product/ops events go to PostHog only — they must never
 * reach GA4 or Meta.
 */
export interface EventRouting {
  ga4: boolean
  posthog: boolean
  meta?: MetaDestination
}

export const EVENT_ROUTING: Record<AnalyticsEventName, EventRouting> = {
  [AnalyticsEvent.SignUp]: {
    ga4: true,
    posthog: true,
    meta: { name: 'CompleteRegistration' },
  },
  [AnalyticsEvent.Login]: { ga4: true, posthog: true },
  [AnalyticsEvent.Search]: {
    ga4: true,
    posthog: true,
    meta: { name: 'Search' },
  },
  [AnalyticsEvent.ViewItemList]: {
    ga4: true,
    posthog: true,
    meta: { name: 'ViewContent', requiresContentType: true },
  },
  [AnalyticsEvent.ViewItem]: {
    ga4: true,
    posthog: true,
    meta: { name: 'ViewContent' },
  },
  [AnalyticsEvent.AddToCart]: {
    ga4: true,
    posthog: true,
    meta: { name: 'AddToCart' },
  },
  [AnalyticsEvent.RemoveFromCart]: { ga4: true, posthog: true },
  [AnalyticsEvent.ViewCart]: { ga4: true, posthog: true },
  [AnalyticsEvent.ViewPromotion]: { ga4: true, posthog: true },
  [AnalyticsEvent.SelectPromotion]: { ga4: true, posthog: true },
  [AnalyticsEvent.BeginCheckout]: {
    ga4: true,
    posthog: true,
    meta: { name: 'InitiateCheckout' },
  },
  [AnalyticsEvent.AddShippingInfo]: { ga4: true, posthog: true },
  [AnalyticsEvent.AddPaymentInfo]: {
    ga4: true,
    posthog: true,
    meta: { name: 'AddPaymentInfo' },
  },
  [AnalyticsEvent.Purchase]: {
    ga4: true,
    posthog: true,
    meta: { name: 'Purchase' },
  },
  [AnalyticsEvent.EarnVirtualCurrency]: {
    ga4: true,
    posthog: true,
    meta: { name: 'EarnVirtualCurrency', custom: true },
  },
  [AnalyticsEvent.PaymentFailed]: { ga4: true, posthog: true },
  [AnalyticsEvent.GenerateLead]: { ga4: false, posthog: true },
  [AnalyticsEvent.Contact]: { ga4: false, posthog: true },
  [AnalyticsEvent.CustomizeProduct]: { ga4: false, posthog: true },
}
